import { useLayoutEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import type { ThreeEvent } from "@react-three/fiber";
import { useUIStore } from "../stores/uiStore";
import { useTelemetryStore } from "../stores/telemetryStore";
import { STATUS_COLOR } from "./palletLoad";
import { xrayRef } from "./xray";
import { deckBasis, deckGroups, deckMatrix, type DeckGroup, type DeckItem } from "./deckInstances";

// The status palette and load math moved to palletLoad.ts with ADR 191 (the
// GLB load layer consumes them too); re-exported so existing imports hold.
export { STATUS_COLOR, UNIDENTIFIED_COLOR, loadOf, palletRect } from "./palletLoad";

/**
 * One active `storage_location`, as returned by the occupancy endpoint
 * (`GET …/inventory/occupancy?runId&groupBy=location`). `polygon` is
 * `[[x,y], …]` meters in the warehouse frame — identity `map_to_warehouse`
 * for a SLAM-promoted model (ADR 98), so P0 renders it directly with no
 * extra transform.
 */
export type OccupancyCell = {
  /** Storage-location id ({mapId}|{address}) — the ADR 186 height-PATCH handle.
   *  Optional: absent on payloads older than ADR 190. */
  id?: string;
  /** ADR 198: the run whose scans this cell reports — under a multi-run merge
   *  the per-address winner. Null on not_scanned cells. */
  runId?: string | null;
  address: string;
  status: "counted" | "empty" | "not_scanned" | "unidentified";
  product?: string;
  /** ADR 196: `product` under the warehouse's alias translation — the WMS item
   *  code when an alias maps it, the raw scanned label otherwise. Only on
   *  counted cells. */
  item?: string;
  quantity?: number;
  polygon: number[][];
  aisleLabel?: string;
  /** ADR 155: which rack level, and the height of the bin's floor. Null on a map
   *  imported without the level enrichment — the layer then keeps its flat
   *  floor-plan reading rather than guessing an elevation. */
  level?: number | null;
  elevationM?: number | null;
  clearHeightM?: number | null;
  /** ADR 322: which tier `elevationM` came from. `location`/`level` = somebody measured
   *  or curated it, `floor` = the level rests on the slab, `nominal`/`default` = nobody
   *  ever said, and the number above is `(level − origin) × pitch`. Null on floor bins
   *  and on payloads older than ADR 322. */
  elevationSource?: "location" | "level" | "floor" | "nominal" | "default" | null;
  /** ADR 154: load height the kit's rack sensor measured, metres. Null where it
   *  did not fire — which is most reads. */
  loadHeightM?: number | null;
  /** ADR 203: WMS date enrichment for the ADDRESS (from the warehouse's current
   *  expected-stock snapshot), regardless of scan status — entry (dwell anchor),
   *  manufacturing and expiry, ISO yyyy-mm-dd. Null where the snapshot has none. */
  fifoDate?: string | null;
  mfgDate?: string | null;
  expiryDate?: string | null;
  /** ADR 425: 2 on a double-deep bin (two pallet positions, front = aisle side);
   *  absent = one. `pallets` then lists each physical pallet at its depth. */
  depthSlots?: number;
  pallets?: DepthPallet[];
};

/** ADR 425: one physical pallet of a double-deep bin. */
export type DepthPallet = {
  depth: "front" | "back";
  status: "counted" | "unidentified";
  /** ADR 428: identity copied from the front pallet by the double-deep rule, not read. */
  inferred?: boolean;
  product?: string;
  item?: string;
  quantity?: number;
};

interface Props {
  locations: OccupancyCell[];
  /** ADR 196 product search. Null/undefined = no search: render normally.
   *  A set (even empty) = search active: matched decks brighten, the rest
   *  ghost to near-transparency so the hits carry the scene. */
  matchedAddresses?: Set<string> | null;
  /** ADR 324 — addresses an in-progress run has reached. Drawn as coverage, never as
   *  stock: what is IN them still comes from the finished run's paint. */
  visitedAddresses?: ReadonlySet<string> | null;
  /** ADR 198 movement focus — the bin(s) a picked label moved FROM, drawn amber
   *  whatever the search state says about them. */
  originAddresses?: ReadonlySet<string> | null;
  /** ADR 354 paint-by classifier — a color paints the cell, null mutes it.
   *  Null/undefined = paint by scan status (the default). */
  paint?: ((cell: OccupancyCell) => string | null) | null;
}

/**
 * Just above the 0.02 m floor overlays (HeatmapLayer's texture plane,
 * Warehouse.tsx's ZoneFloor) so this layer doesn't z-fight with them when
 * both are visible at once.
 */
const FLOOR_Y = 0.03;

/** Shared unit-square deck geometry, translated so the instance basis maps its
 *  [0..1]² onto the bin polygon (PlaneGeometry is centred by default). */
const UNIT_PLANE = new THREE.PlaneGeometry(1, 1);
UNIT_PLANE.translate(0.5, 0.5, 0);

/** Search isolate (ADR 196): hits brighten, everything else drops to a faint
 *  footprint — visible enough to keep the aisle's shape. Decks are flat tints,
 *  so they render UNLIT (meshBasicMaterial, full token color): at 15k
 *  mostly-overlapping transparent quads the PBR fragment cost alone halved the
 *  frame rate on an integrated GPU (ADR 197). */
const DECK_LOOK = {
  normal: { opacity: 0.55 },
  match:  { opacity: 0.9 },
  ghost:  { opacity: 0.07 },
  visited: { opacity: 0.95 },
  origin:  { opacity: 0.95 },
  /** ADR 354: cells outside the active paint-by story (empty / not-scanned under
   *  Validade). Faint like a ghost but a shade stronger — the aisle keeps its
   *  shape without competing with the ramp. */
  muted:  { opacity: 0.10 },
} as const;

/** ADR 324: a live-visited deck is painted in ONE colour rather than its status tint —
 *  the statement is "the kit has been here", which is about the sweep, not about what the
 *  last finished run found in the bin. Reusing a status colour would say something the
 *  live plane is explicitly not allowed to say. */
const VISITED_COLOR = "#1E5BD8";  // --op-blue-500
/** ADR 198 movement focus: where the picked label came from. Amber (--op-warn)
 *  is no scan status and not the live-visited blue, so it cannot be misread as
 *  either; the destination keeps its status colour as the search hit. */
const ORIGIN_COLOR = "#D97706";   // --op-warn
const UNVISITED_OPACITY = 0.4;

/**
 * One InstancedMesh per (status × search-state) group of bins (ADR 197): the
 * JSL warehouse's 15 538 bins rendered at 1 FPS as per-cell meshes; as at most
 * 8 instanced draws the deck cost stops scaling with the warehouse. Each
 * instance's matrix maps the unit square onto its bin polygon (deckBasis) at
 * the bin's own elevation.
 */
function DeckInstances({ group, onPick, onHover }: {
  group: DeckGroup;
  onPick: (cell: OccupancyCell) => void;
  onHover: (item: DeckItem | null) => void;
}) {
  const ref = useRef<THREE.InstancedMesh>(null);
  // ADR 371: a ghosted deck (search active, no match) is dead to the pointer —
  // no handlers at all, so r3f never raycasts it and a click on it lands on
  // whatever is behind, or on nothing, which clears the selection like any
  // click on empty floor. Groups are keyed by kind, so this never flips on a
  // mounted mesh.
  const { items, status, kind } = group;

  useLayoutEffect(() => {
    const mesh = ref.current;
    if (!mesh || typeof mesh.setMatrixAt !== "function") return; // jsdom renders the tag only
    const m = new THREE.Matrix4();
    items.forEach((item, i) => {
      m.set(...(deckMatrix(item) as Parameters<THREE.Matrix4["set"]>));
      mesh.setMatrixAt(i, m);
    });
    mesh.instanceMatrix.needsUpdate = true;
  }, [items]);

  const color = group.color ?? (kind === "visited" ? VISITED_COLOR : kind === "origin" ? ORIGIN_COLOR : STATUS_COLOR[status]);
  const look = DECK_LOOK[kind];
  // ADR 428: not visited is a quiet backdrop, fainter than a counted deck. Yellow can
  // fade without turning carton-brown (red and green stay level), which amber could not.
  const opacity = kind === "normal" && status === "not_scanned" && !group.color ? UNVISITED_OPACITY : look.opacity;
  return (
    <instancedMesh
      ref={ref}
      args={[undefined, undefined, items.length]}
      // Instance transforms spread across the warehouse; the shared unit-plane
      // bounds would cull the whole group as soon as the origin leaves view.
      frustumCulled={false}
      {...(kind !== "ghost" && {
        // ADR 190: pick the bin → InspectorPanel's bin branch. stopPropagation
        // keeps Canvas.onPointerMissed from immediately clearing it.
        onClick: (e: ThreeEvent<MouseEvent>) => {
          e.stopPropagation();
          // r3f always sets instanceId on an instanced hit; the ?? 0 keeps the
          // wiring exercisable from DOM-level tests, where no raycast runs.
          onPick(items[e.instanceId ?? 0].cell);
        },
        onPointerOver: (e: ThreeEvent<PointerEvent>) => { e.stopPropagation(); document.body.style.cursor = "pointer"; },
        // ADR 279: move, not over — inside one InstancedMesh the hovered
        // INSTANCE changes without a new pointerover, and the ghost must follow.
        onPointerMove: (e: ThreeEvent<PointerEvent>) => {
          e.stopPropagation();
          onHover(items[e.instanceId ?? 0] ?? null);
        },
        onPointerOut: () => { document.body.style.cursor = "auto"; onHover(null); },
      })}
    >
      <primitive object={UNIT_PLANE} attach="geometry" />
      <meshBasicMaterial
        ref={xrayRef}
        color={color}
        transparent
        opacity={opacity}
        depthWrite={false}
        side={THREE.DoubleSide}
      />
    </instancedMesh>
  );
}

/**
 * Renders `storage_location` bins colored by scan status, instanced per
 * (status × search-state) group (ADR 197 — the per-cell mesh version died at
 * JSL scale).
 *
 * Two readings, chosen per cell by whether the map knows its rack levels
 * (ADR 155):
 *
 * * **flat** — `elevationM` null: the original floor-plan tint, every bin at
 *   `FLOOR_Y`. A single-level map, or one imported before the enrichment.
 * * **racked** — `elevationM` set: each bin's tint sits at its own height, so
 *   the aisle reads as the rack it is. The goods themselves — pallet + carton
 *   stack — are PalletLoadLayer's (ADR 191), drawn from the same cells.
 *
 * The tints live in a `rotation-x = -π/2` group, whose local +Z is world +Y —
 * so elevation rides each instance's local z, and deckBasis negates y exactly
 * as polygonToShape did (see rackVolume.ts for the frame contract).
 */
/** Lift the hover ghost a hair above its deck (local z = the bin's "up" in the
 *  rotated group) so the glow never z-fights the very cell it highlights. */
const GHOST_LIFT = new THREE.Matrix4().makeTranslation(0, 0, 0.004);

export function OccupancyLayer({ locations, matchedAddresses, visitedAddresses, originAddresses, paint }: Props) {
  const setSelectedBin = useUIStore((s) => s.setSelectedBin);
  const clearAssetSelection = useTelemetryStore((s) => s.clearSelection);
  // ADR 279: one white translucent quad follows the hovered bin, driven
  // IMPERATIVELY — pointermove at 60 Hz over 15k instanced cells must never
  // set React state (a re-render per move is the remount storm ADR 197 killed).
  const ghostRef = useRef<THREE.Mesh>(null);
  const ghostMatrix = useRef(new THREE.Matrix4());
  const hoverGhost = (item: DeckItem | null) => {
    const g = ghostRef.current;
    if (!g || typeof g.matrix?.copy !== "function") return; // jsdom renders the tag only
    if (!item) { g.visible = false; return; }
    const m = ghostMatrix.current;
    m.set(...(deckMatrix(item) as Parameters<THREE.Matrix4["set"]>));
    m.multiply(GHOST_LIFT);
    g.matrix.copy(m);
    g.visible = true;
  };

  const items = useMemo(
    () => locations.flatMap((cell): DeckItem[] => {
      const basis = deckBasis(cell.polygon);
      return basis ? [{ cell, basis }] : [];
    }),
    [locations],
  );
  const groups = useMemo(
    () => deckGroups(items, matchedAddresses ?? null, visitedAddresses ?? null, paint ?? null, originAddresses ?? null),
    [items, matchedAddresses, visitedAddresses, paint, originAddresses],
  );

  const pick = (cell: OccupancyCell) => {
    // Clearing the asset selection makes the inspector's precedence
    // deterministic (most recent click wins — see InspectorPanel).
    clearAssetSelection();
    setSelectedBin(cell);
  };

  return (
    <group rotation={[-Math.PI / 2, 0, 0]} position={[0, FLOOR_Y, 0]}>
      {groups.map((g) => (
        // Keyed on the group's size too: an InstancedMesh's capacity is fixed at
        // construction, so a membership change remounts rather than under-draws.
        <DeckInstances key={`${g.key}:${g.items.length}`} group={g} onPick={pick} onHover={hoverGhost} />
      ))}
      {/* ADR 279: the hover ghost — same unit-plane geometry, positioned onto
          the hovered bin via its deck matrix. raycast off: the affordance must
          never swallow the click it is advertising. */}
      <mesh
        ref={ghostRef}
        matrixAutoUpdate={false}
        visible={false}
        frustumCulled={false}
        raycast={() => null}
      >
        <primitive object={UNIT_PLANE} attach="geometry" />
        <meshBasicMaterial color="#ffffff" transparent opacity={0.22} depthWrite={false} side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
}
